/**
 * Toast Notification Utilities
 * Wrapper around sonner for consistent toast notifications
 */

import { toast as sonnerToast } from 'sonner';

/**
 * Success toast notification
 */
export function success(message: string, description?: string) {
  return sonnerToast.success(message, {
    description,
    duration: 4000,
  });
}

/**
 * Error toast notification
 */
export function error(message: string, description?: string) {
  return sonnerToast.error(message, {
    description,
    duration: 6000,
  });
}

/**
 * Warning toast notification
 */
export function warning(message: string, description?: string) {
  return sonnerToast.warning(message, {
    description,
    duration: 5000,
  });
}

/**
 * Info toast notification
 */
export function info(message: string, description?: string) {
  return sonnerToast.info(message, {
    description,
    duration: 4000,
  });
}

/**
 * Loading toast notification
 * Returns ID that can be used to update/dismiss the toast
 */
export function loading(message: string, description?: string) {
  return sonnerToast.loading(message, {
    description,
  });
}

/**
 * Promise toast - shows loading, then success/error based on promise result
 */
export function promise<T>(
  promise: Promise<T>,
  options: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: unknown) => string);
  }
) {
  return sonnerToast.promise(promise, options);
}

/**
 * Dismiss a toast by ID
 */
export function dismiss(toastId?: string | number) {
  if (toastId) {
    sonnerToast.dismiss(toastId);
  } else {
    sonnerToast.dismiss();
  }
}

/**
 * Custom toast with custom configuration
 */
export function custom(message: string, options?: Parameters<typeof sonnerToast>[1]) {
  return sonnerToast(message, options);
}

// Export the entire toast object for advanced usage
export const toast = {
  success,
  error,
  warning,
  info,
  loading,
  promise,
  dismiss,
  custom,
};

export default toast;
